import React from 'react';
import styled from 'styled-components';

// Styled Components
const SectionWrapper = styled.section`
  padding: 100px 0;
  background-color: var(--bg-light);
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  position: relative;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 75px;
    left: 60px;
    right: 60px;
    height: 2px;
    background: var(--primary);
    z-index: 0;
    
    @media (max-width: 992px) {
      display: none;
    }
  }
`;

const StepCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const StepNumberWrapper = styled.div`
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  background: white;
  border-radius: 50%;
  box-shadow: var(--shadow-md);
`;

const StepIcon = styled.div`
  width: 80px;
  height: 80px;
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.75rem;
`;

const StepDescription = styled.p`
  color: var(--text-light);
`;

// SVG Components for each step
const ConsultationIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="40" cy="40" r="39" stroke="#4338CA" strokeWidth="2" />
        <text x="40" y="48" fontFamily="Arial" fontSize="32" fontWeight="bold" fill="#4338CA" textAnchor="middle">1</text>

        {/* Two people discussing */}
        <path d="M25,25 C28,25 30,22 30,19 C30,16 28,13 25,13 C22,13 20,16 20,19 C20,22 22,25 25,25 Z" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <path d="M55,25 C58,25 60,22 60,19 C60,16 58,13 55,13 C52,13 50,16 50,19 C50,22 52,25 55,25 Z" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <path d="M25,25 L25,55 L35,55 L35,40 L45,40 L45,55 L55,55 L55,25" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <path d="M30,30 L50,30" stroke="#4338CA" strokeWidth="1.5" strokeDasharray="2 2" />
        <path d="M30,35 L50,35" stroke="#4338CA" strokeWidth="1.5" strokeDasharray="2 2" />
    </svg>
);

const CustomizationIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="40" cy="40" r="39" stroke="#4338CA" strokeWidth="2" />
        <text x="40" y="48" fontFamily="Arial" fontSize="32" fontWeight="bold" fill="#4338CA" textAnchor="middle">2</text>

        {/* Design elements */}
        <rect x="20" y="20" width="40" height="25" rx="2" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <rect x="24" y="24" width="32" height="3" rx="1" fill="#4338CA" />
        <rect x="24" y="30" width="32" height="11" rx="1" fill="#F3F4F6" />
        <circle cx="30" cy="35" r="3" fill="#4338CA" />
        <circle cx="40" cy="35" r="3" fill="#8B5CF6" />
        <circle cx="50" cy="35" r="3" fill="#F59E0B" />

        {/* Paintbrush */}
        <path d="M50,48 C55,45 60,50 55,55 L45,65 L35,55 L45,45 L50,48 Z" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <path d="M43,47 L37,53" stroke="#4338CA" strokeWidth="1.5" />
    </svg>
);

const IntegrationIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="40" cy="40" r="39" stroke="#4338CA" strokeWidth="2" />
        <text x="40" y="48" fontFamily="Arial" fontSize="32" fontWeight="bold" fill="#4338CA" textAnchor="middle">3</text>

        {/* Integration/connection visualization */}
        <rect x="20" y="20" width="15" height="15" rx="2" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <rect x="45" y="20" width="15" height="15" rx="2" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <rect x="20" y="45" width="15" height="15" rx="2" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <rect x="45" y="45" width="15" height="15" rx="2" stroke="#4338CA" strokeWidth="1.5" fill="none" />

        {/* Connection lines */}
        <path d="M35,27.5 L45,27.5" stroke="#4338CA" strokeWidth="1.5" />
        <path d="M27.5,35 L27.5,45" stroke="#4338CA" strokeWidth="1.5" />
        <path d="M35,52.5 L45,52.5" stroke="#4338CA" strokeWidth="1.5" />
        <path d="M52.5,35 L52.5,45" stroke="#4338CA" strokeWidth="1.5" />
        <circle cx="40" cy="40" r="5" stroke="#4338CA" strokeWidth="1.5" fill="#F3F4F6" />
    </svg>
);

const SupportIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="40" cy="40" r="39" stroke="#4338CA" strokeWidth="2" />
        <text x="40" y="48" fontFamily="Arial" fontSize="32" fontWeight="bold" fill="#4338CA" textAnchor="middle">4</text>

        {/* Support visualization */}
        <path d="M40,20 C30,20 20,28 20,40 C20,52 30,60 40,60 C50,60 60,52 60,40 C60,28 50,20 40,20 Z" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <path d="M40,45 L40,35" stroke="#4338CA" strokeWidth="1.5" />
        <circle cx="40" cy="30" r="1.5" fill="#4338CA" />

        {/* Headset icon */}
        <path d="M30,38 L30,42 C30,46 32,48 35,48 L40,48 L45,48 C48,48 50,46 50,42 L50,38" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <path d="M30,38 C30,34 31,32 35,32 L45,32 C49,32 50,34 50,38" stroke="#4338CA" strokeWidth="1.5" fill="none" />
        <path d="M40,48 L40,52" stroke="#4338CA" strokeWidth="1.5" />
        <path d="M36,52 L44,52" stroke="#4338CA" strokeWidth="1.5" />
    </svg>
);

// Step data
const steps = [
    {
        id: 1,
        icon: <ConsultationIcon />,
        title: "諮詢與規劃",
        description: "我們的專家團隊了解您的業務需求，規劃最適合的解決方案。"
    },
    {
        id: 2,
        icon: <CustomizationIcon />,
        title: "客製化設計",
        description: "依照您的品牌調整介面設計、功能和流程，打造專屬應用。"
    },
    {
        id: 3,
        icon: <IntegrationIcon />,
        title: "系統整合",
        description: "與您現有的ERP、CRM或支付系統無縫整合，確保數據同步。"
    },
    {
        id: 4,
        icon: <SupportIcon />,
        title: "上線與支援",
        description: "應用上線後持續提供技術支援和系統更新，確保穩定運行。"
    }
];

const HowItWorks = () => {
    return (
        <SectionWrapper id="how-it-works">
            <SectionContainer>
                <SectionHeader>
                    <SectionTitle>簡單四步驟，輕鬆上線</SectionTitle>
                    <SectionDescription>
                        我們簡化了整個開發和上線流程，讓您的電商應用更快速地推向市場
                    </SectionDescription>
                </SectionHeader>

                <ProcessSteps>
                    {steps.map((step) => (
                        <StepCard key={step.id}>
                            <StepNumberWrapper>
                                <StepIcon>
                                    {step.icon}
                                </StepIcon>
                            </StepNumberWrapper>
                            <StepTitle>{step.title}</StepTitle>
                            <StepDescription>{step.description}</StepDescription>
                        </StepCard>
                    ))}
                </ProcessSteps>
            </SectionContainer>
        </SectionWrapper>
    );
};

export default HowItWorks;